import { Component, ElementRef, inject, ViewChild } from '@angular/core'
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { PasswordChangeService } from '../../services/password-change.service'
import { SnackBarService } from '../../services/snackbar.service'
import { environment } from '../../../environments/environment'

interface PasswordForm {
    currentPassword: FormControl<string | null>
    newPassword: FormControl<string | null>
    confirmPassword: FormControl<string | null>
}

@Component({
    selector: 'app-password-change',
    templateUrl: './password-change.component.html',
    styleUrls: ['./password-change.component.scss'],
    imports: [ReactiveFormsModule, TranslateModule],
})
export class PasswordChangeComponent {
    form: FormGroup
    hideCurrent = true
    hideNew = true
    hideConfirm = true
    minPasswordLength: number

    private snackbarService = inject(SnackBarService)
    @ViewChild('dialog') private dialogEl!: ElementRef<HTMLDialogElement>

    constructor(
        private fb: FormBuilder,
        private passwordChangeService: PasswordChangeService,
        private translate: TranslateService,
    ) {
        this.minPasswordLength = environment.minPasswordLength
        this.form = this.fb.group<PasswordForm>(
            {
                currentPassword: this.fb.control('', { validators: Validators.required, nonNullable: true }),
                newPassword: this.fb.control('', {
                    validators: [
                        Validators.required,
                        Validators.minLength(this.minPasswordLength),
                        this.passwordComplexityValidator.bind(this),
                    ],
                    nonNullable: true,
                }),
                confirmPassword: this.fb.control('', {
                    validators: [
                        Validators.required,
                        Validators.minLength(this.minPasswordLength),
                        this.passwordComplexityValidator.bind(this),
                    ],
                    nonNullable: true,
                }),
            },
            { validators: this.passwordMatchValidator },
        )
    }

    open() {
        this.form.reset()
        this.dialogEl.nativeElement.showModal()
    }

    onSave() {
        if (!this.form.valid) return
        const { currentPassword, newPassword, confirmPassword } = this.form.value
        this.passwordChangeService.changePassword(currentPassword, newPassword, confirmPassword).subscribe({
            complete: () => {
                this.dialogEl.nativeElement.close()
                this.translate.get('PASSWORD_UPDATED').subscribe((msg: string) => {
                    this.snackbarService.showSuccess(msg)
                })
            },
        })
    }

    onCancel() {
        this.dialogEl.nativeElement.close()
    }

    preventSpaces(event: KeyboardEvent) {
        if (event.key === ' ') event.preventDefault()
    }

    private passwordMatchValidator = (form: AbstractControl): ValidationErrors | null => {
        const np = form.get('newPassword')?.value
        const cp = form.get('confirmPassword')?.value
        if (np !== cp) return { mismatch: true }
        if (np.length < this.minPasswordLength || cp.length < this.minPasswordLength) return { minlength: true }
        return null
    }

    passwordComplexityValidator(control: AbstractControl): ValidationErrors | null {
        const v = control.value
        if (!v) return null
        const ok =
            /[A-Z]/.test(v) &&
            /[a-z]/.test(v) &&
            /[0-9]/.test(v) &&
            /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(v)
        return ok ? null : { complexity: true }
    }
}
